import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import styles from './Decision.module.css';
import FormalOfferCard from './FormalOfferCard';
import offeredAnimation from '../../assets/offeredAnimation.gif';
import { ISOLATED_SPLIT_TESTS } from '../../constants';
import { getDurationInMonths } from '../../helpers/product.helpers';
import { TGetFormalOffersResponse } from '../../types';
import { ACCOUNT_PAGES_URL } from '../../urls';
import { Button } from '../Button/Button';

type TFormalOffer = TGetFormalOffersResponse['data']['formal_offers'][number];

type TProps = {
  formalOffersData: TGetFormalOffersResponse['data'];
  splitTestBranch?: 'INCLUDE' | 'EXCLUDE' | null;
};

const getMinimumMonthlyRepayment = (offer?: TFormalOffer) =>
  offer?.minimum_monthly_repayment;

/**
 * Variant only: pick the offer with the lowest minimum_monthly_repayment as the
 * lead (the longest duration yields the lowest repayment), shown as "Up to X".
 * The control keeps rendering formal_offers[0], so the A/B measures cost framing
 * alone.
 */
const getLeadOffer = (offers: TFormalOffer[]): TFormalOffer => {
  const offersWithRepayment = offers.filter(
    (offer) => getMinimumMonthlyRepayment(offer) !== undefined,
  );

  if (offersWithRepayment.length === 0) {
    return offers[0];
  }

  return offersWithRepayment.reduce((lowest, offer) =>
    (getMinimumMonthlyRepayment(offer) as number) <
    (getMinimumMonthlyRepayment(lowest) as number)
      ? offer
      : lowest,
  );
};

const getLongestTermInMonths = (offers: TFormalOffer[]): number | null =>
  offers.reduce<number | null>((longest, offer) => {
    const months = getDurationInMonths(offer.duration);
    if (months === null) {
      return longest;
    }
    return longest === null || months > longest ? months : longest;
  }, null);

const OfferedDecision = ({ formalOffersData, splitTestBranch }: TProps) => {
  const isVariant = splitTestBranch === 'INCLUDE';

  useEffect(() => {
    // Fire an exposure event for BOTH arms so the A/B test has a control
    // denominator to compare against.
    if (splitTestBranch === 'INCLUDE' || splitTestBranch === 'EXCLUDE') {
      tracking.ampli.addedToSplitTest({
        Branch: splitTestBranch,
        'Test name':
          ISOLATED_SPLIT_TESTS.PARTNER_DECISION_PAGE_COST_FRAMING_2026,
      });
    }
  }, [splitTestBranch]);

  const offers = formalOffersData?.formal_offers ?? [];
  const leadOffer = isVariant ? getLeadOffer(offers) : offers[0];
  const longestTermInMonths = isVariant ? getLongestTermInMonths(offers) : null;
  const minimumMonthlyRepayment = isVariant
    ? getMinimumMonthlyRepayment(leadOffer)
    : undefined;

  return (
    <div className={styles.container}>
      {isVariant ? (
        <>
          <h1 className={styles.heading}>
            You’re ready to
            <br />
            draw down your offer
          </h1>
          <h2 className={styles.subHeading}>
            Head to iwoca to review your offer
            <br />
            and take your funds.
          </h2>
        </>
      ) : (
        <>
          <h1 className={styles.heading}>
            You’re ready to draw down your offer
          </h1>
          <h2 className={styles.subHeading}>
            Head to iwoca to review your offer and take your funds.
          </h2>
        </>
      )}
      <div className={styles.gifContainer}>
        <img data-testid="offered-animation" src={offeredAnimation} alt="" />
      </div>
      <FormalOfferCard
        {...leadOffer}
        isVariant={isVariant}
        termInMonths={longestTermInMonths}
        minimum_monthly_repayment={minimumMonthlyRepayment}
      />
      <div className={styles.buttonContainer}>
        <Button href={ACCOUNT_PAGES_URL} size="large">
          {isVariant ? 'Continue to your offer' : 'Review your offer'}
        </Button>
      </div>
    </div>
  );
};

export default OfferedDecision;
